import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SelectionModel } from '@angular/cdk/collections';

import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatCardModule } from '@angular/material/card';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';

import { stringEquals } from '@utils/string.util';
import { tagStyle } from '@utils/tag-color.util';

import {
  UserTypeViewModel,
  TenantViewModel,
  RoleViewModel,
  DeviceTag,
} from '../account-list.component';
import { FilterService } from '@services/filter.service';
import { format } from 'date-fns';
import { DirectivesModule } from 'src/app/directive/auto-complete-off/auto-complete-off.module';

/** アカウントのViewModel */
export type AccountViewModel = {
  /** ID */
  userId: string;
  /** メールアドレス */
  email: string;
  /** 姓 */
  familyName: string;
  /** 名 */
  givenName: string;
  /** セイ */
  familyNameKana: string;
  /** メイ */
  givenNameKana: string;
  /** メールアドレス認証ステータス */
  emailStatus: boolean;
  /** 2段階認証必須か */
  mfaRequired: boolean;
  /** 2段階認証ステータス */
  mfaStatus: boolean;
  /** 最終ログイン日時 */
  lastLoginAt: string;
  /** テナント */
  tenant: TenantViewModel;
  /** ユーザー種別 */
  userType: UserTypeViewModel;
  /** 設定権限 */
  configurable?: boolean;
  /** タグID一覧 */
  deviceTagList?: string[];
};

interface AccountsFilter {
  tenantName?: string;
  userName?: string;
  email?: string;
  emailStatus?: boolean;
  mfaRequiredStatus?: boolean;
  mfaStatus?: boolean;
  configurable?: boolean;
  lastLoginAt?: string;
  deviceTag?: string;
  sortState?: Sort;
  pageIndex?: number;
}

@Component({
  selector: 'app-account-list-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatIconModule,
    MatCheckboxModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatCardModule,
    DatePipe,
    DirectivesModule,
  ],
  templateUrl: './account-list-table.component.html',
  styleUrl: './account-list-table.component.scss',
})
export class AccountListTableComponent
  implements OnInit, AfterViewInit, OnChanges
{
  public readonly tagStyle = tagStyle;
  /** アカウント一覧 */
  @Input({ required: true }) accountList: AccountViewModel[] = [];

  /** テナント一覧 */
  @Input({ required: true }) tenantList: TenantViewModel[] = [];

  /** タグ一覧 */
  @Input({ required: true }) deviceTagList: DeviceTag[] = [];

  /** ログインユーザー権限 */
  @Input({ required: true }) loggedInUserRole?: RoleViewModel;

  /** ユーザー種別 */
  @Input({ required: true }) selectedUserType: UserTypeViewModel =
    'tenant_admin';

  /** 閲覧のみか */
  @Input() readonly = false;

  /** 新規登録を通知 */
  @Output() created = new EventEmitter<void>();

  /** 更新を通知 */
  @Output() updated = new EventEmitter<AccountViewModel>();

  /** 削除を通知 */
  @Output() deleted = new EventEmitter<AccountViewModel[]>();

  /** ページネーション要素 */
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  /** ソート要素 */
  @ViewChild(MatSort) sort!: MatSort;

  /** カラム */
  public displayedColumns: string[] = [];

  /** 一覧情報 */
  public dataSource = new MatTableDataSource<AccountViewModel>([]);

  /** 選択状態 */
  public selection = new SelectionModel<AccountViewModel>(true, []);

  /** テナント名検索キーワード */
  public tenantNameKeyword = '';

  /** 選択されたテナント */
  public selectedTenant: string = '';

  /** ユーザー名検索キーワード */
  public userNameKeyword = '';

  /** メール検索キーワード */
  public emailKeyword = '';

  /** 選択されたメールステータス */
  public selectedEmailStatus: boolean | undefined;

  /** 選択されたMFA設定 */
  public selectedMfaRequired: boolean | undefined;

  /** 選択されたMFAステータス */
  public selectedMfaStatus: boolean | undefined;

  /** 選択された設定権限 */
  public selectedConfigurable: boolean | undefined;

  /** 閲覧可能なデバイスタグ検索キーワード */
  public deviceTagKeyword = '';

  /** 最終ログイン日検索キーワード */
  public lastLoginAtKeyword = '';

  /** 選択可能なデバイスタグ一覧 */
  public selectableDeviceTagList: string[] = [];

  /** 選択されたデバイスタグ一覧 */
  public selectedDeviceTagList: string[] = [];

  /** 選択可能なデバイス一覧 */
  public selectableDeviceList: string[] = [];

  /** 選択されたデバイス一覧 */
  public selectedDeviceList: string[] = [];

  /** 初期ソート値 */
  public initSortState: Sort = {
    active: 'userName',
    direction: 'asc',
  };

  /** 初期ユーザー種別 */
  private initUserType: string | undefined;

  /** ソート状態 */
  private sortState: Sort | undefined;

  /** ページ番号 */
  private pageIndex: number | undefined;

  constructor(
    private filterService: FilterService<AccountsFilter, AccountViewModel>,
    private cdr: ChangeDetectorRef,
  ) {
    this.initUserType = this.selectedUserType;
    this.filterService.setSessionStorageKey(this.initUserType);
  }

  public ngOnInit(): void {
    this._setDisplayedColumns(this.selectedUserType, this.loggedInUserRole);

    // フィルター条件の取得と適用
    this.loadFilters();
  }

  public ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;

    this.dataSource.sort = this.sort;

    // ソート設定
    this.dataSource.sortingDataAccessor = (
      item: AccountViewModel,
      property: string,
    ) => {
      if (property === 'userName') {
        return this.getFullNameToCompare(item);
      }

      if (property === 'tenant') {
        return item.tenant.tenantName || '';
      }

      const value = item[property as keyof AccountViewModel];
      return value == null ? '' : `${value}`;
    };

    // ソートとページングの変更を検知する
    this.filterService.monitorSortAndPagination(this.sort, this.paginator);

    // ソートとページングの設定
    this.setFilters();

    // フィルター設定
    this.dataSource.filterPredicate = (element: AccountViewModel) => {
      let tenantName = true;
      if (this.selectedTenant) {
        // deviceTypeIdKeywordが空でない場合のみフィルタリングを行う
        tenantName =
          element.tenant.tenantName === undefined
            ? false
            : element.tenant.tenantName === this.selectedTenant;
      }
      let userName = true;
      if (this.userNameKeyword) {
        // userNameKeywordが空でない場合のみフィルタリングを行う
        const userNameKanji = stringEquals(
          element.familyName + element.givenName,
          this.userNameKeyword,
        );
        const userNameKana = stringEquals(
          element.familyNameKana + element.givenNameKana,
          this.userNameKeyword,
        );
        userName = userNameKanji || userNameKana;
      }
      let email = true;
      if (this.emailKeyword) {
        // emailKeywordが空でない場合のみフィルタリングを行う
        email = stringEquals(element.userId, this.emailKeyword);
      }
      let emailStatus = true;
      if (
        this.selectedEmailStatus !== undefined &&
        this.selectedEmailStatus !== null
      ) {
        // selectedEmailStatusが空でない場合のみフィルタリングを行う
        emailStatus = element.emailStatus === this.selectedEmailStatus;
      }
      let mfaRequiredStatus = true;
      if (
        this.selectedMfaRequired !== undefined &&
        this.selectedMfaRequired !== null
      ) {
        // selectedMfaRequiredが空でない場合のみフィルタリングを行う
        mfaRequiredStatus = element.mfaRequired === this.selectedMfaRequired;
      }
      let mfaStatus = true;
      if (
        this.selectedMfaStatus !== undefined &&
        this.selectedMfaStatus !== null
      ) {
        // selectedMfaStatusが空でない場合のみフィルタリングを行う
        mfaStatus = element.mfaStatus === this.selectedMfaStatus;
      }
      let configurable = true;
      if (
        this.selectedConfigurable !== undefined &&
        this.selectedConfigurable !== null
      ) {
        // selectedConfigurableが空でない場合のみフィルタリングを行う
        configurable = element.configurable === this.selectedConfigurable;
      }
      let deviceTag = true;
      if (this.deviceTagKeyword) {
        if (element.deviceTagList) {
          deviceTag = element.deviceTagList.some((tagId) =>
            this.deviceTagList.some(
              (tag) =>
                tag.tagId === tagId &&
                stringEquals(tag.tagName, this.deviceTagKeyword),
            ),
          );
        } else {
          deviceTag = false;
        }
      }
      let lastLoginAt = true;
      if (this.lastLoginAtKeyword) {
        // lastLoginAtが有効な日付であるかを確認
        if (element.lastLoginAt) {
          const formattedLastLoginAt = format(
            new Date(element.lastLoginAt),
            'yyyy/MM/dd HH:mm:ss',
          );

          lastLoginAt =
            (formattedLastLoginAt ?? '')
              .trim()
              .indexOf(this.lastLoginAtKeyword.toLowerCase()) > -1;
        } else {
          // lastLoginAtが空の場合はフィルタリングを無効にする
          lastLoginAt = false;
        }
      }
      // フィルター条件を保持
      this.saveFiltersToStorage();

      return (
        tenantName &&
        userName &&
        email &&
        emailStatus &&
        mfaRequiredStatus &&
        mfaStatus &&
        configurable &&
        lastLoginAt &&
        deviceTag
      );
    };

    this.applyFilter();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['accountList'] !== undefined) {
      const accountList: AccountViewModel[] =
        changes['accountList'].currentValue;
      this._setDataSource(accountList, this.selectedUserType);

      const selectableDeviceTagList: string[] = [];
      accountList.forEach((account) => {
        if (account.deviceTagList) {
          selectableDeviceTagList.push(...account.deviceTagList);
        }
      });
      this.selectableDeviceTagList = Array.from(
        new Set(selectableDeviceTagList),
      );

      const selectableDeviceList: string[] = [];
      accountList.forEach((account) => {
        if (account.deviceTagList) {
          selectableDeviceList.push(...account.deviceTagList);
        }
      });
      this.selectableDeviceList = Array.from(new Set(selectableDeviceList));
    } else if (changes['selectedUserType'] !== undefined) {
      this.filterService.setSessionStorageKey(this.selectedUserType);
      this.resetFilters();

      const userType: UserTypeViewModel =
        changes['selectedUserType'].currentValue;
      this._setDisplayedColumns(userType, this.loggedInUserRole);
      this._setDataSource(this.accountList, userType);

      // フィルター条件の取得と適用
      this.loadFilters();

      // ソートとページングの設定
      this.setFilters();

      this.applyFilter();
    } else if (changes['loggedInUserRole'] !== undefined) {
      const role: RoleViewModel = changes['loggedInUserRole'].currentValue;
      this._setDisplayedColumns(this.selectedUserType, role);
    }
  }

  /**
   * テナントに紐づくデバイスタグをフィルタリングする
   */
  public getValidDeviceTags(
    deviceTagList: DeviceTag[],
    tenantDeviceTagIdList: string[],
  ): DeviceTag[] {
    const tenantTagSet = new Set(tenantDeviceTagIdList);

    // タグをフィルタリングし、重複を排除するためのMapを作成
    const uniqueTagsMap = new Map<string, DeviceTag>();

    deviceTagList.forEach((tag) => {
      if (tenantTagSet.has(tag.tagId)) {
        uniqueTagsMap.set(tag.tagName.toLowerCase(), tag);
      }
    });

    // 一致するタグと一致しないタグに分ける
    const uniqueTags = Array.from(uniqueTagsMap.values());

    const matchedTags = uniqueTags.filter(
      (tag) =>
        this.deviceTagKeyword &&
        tag.tagName.toLowerCase().includes(this.deviceTagKeyword.toLowerCase()),
    );

    const unmatchedTags = uniqueTags.filter(
      (tag) =>
        !this.deviceTagKeyword ||
        !tag.tagName
          .toLowerCase()
          .includes(this.deviceTagKeyword.toLowerCase()),
    );

    // 一致するタグを最初に表示し、残りを昇順で表示
    const sortedUnmatchedTags = unmatchedTags.sort((a, b) =>
      a.tagName.localeCompare(b.tagName),
    );

    return [...matchedTags, ...sortedUnmatchedTags];
  }

  /**
   * すべての行を選択／非選択に切り替える
   */
  public toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }

    this.selection.select(...this.dataSource.filteredData);
  }

  /**
   * すべて選択されているか
   *
   * @returns すべて選択されていればtrue, 非選択があればfalse
   */
  public isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.filteredData.length;
    return numSelected === numRows;
  }

  /**
   * 一括削除する
   */
  public deleteAll() {
    this.deleted.emit(this.selection.selected);
  }

  /**
   * 検索条件（FilterPredicate）に基づいて、フィルタリング処理を適用する。
   */
  public applyFilter(): void {
    // フィルタ処理を発火
    // ※空文字指定だとフィルタリングがされないためdummy文字列を指定している
    // （Angular materialのtable-data-source.tsの_filterDataメソッド参照）
    this.dataSource.filter = 'dummy';
  }

  /**
   * テナントでのフィルタリングをクリアする。
   */
  public clearTenantNameFilter(): void {
    // フィルタ条件をクリア
    this.tenantNameKeyword = '';

    this.applyFilter();
  }

  /**
   * ユーザー名でのフィルタリングをクリアする。
   */
  public clearUserNameFilter(): void {
    // フィルタ条件をクリア
    this.userNameKeyword = '';

    this.applyFilter();
  }

  /**
   * メールアドレスでのフィルタリングをクリアする。
   */
  public clearEmailFilter(): void {
    // フィルタ条件をクリア
    this.emailKeyword = '';

    this.applyFilter();
  }

  /**
   * 閲覧可能タグでのフィルタリングをクリアする。
   */
  public clearDeviceTagFilter(): void {
    // フィルタ条件をクリア
    this.deviceTagKeyword = '';

    this.applyFilter();
  }

  /**
   * 最終ログイン日時でのフィルタリングをクリアする。
   */
  public clearLastLoginAtFilter(): void {
    // フィルタ条件をクリア
    this.lastLoginAtKeyword = '';

    this.applyFilter();
  }

  /**
   * 各フィルターを初期化する。
   */
  public clearFilters() {
    // フィルターを初期化
    this.loadFilters(true);

    const sortState: Sort = this.initSortState;

    this.filterService.setSort(this.dataSource, this.sort, sortState);

    this.setPaginationIndex();

    // フィルターを適用
    this.dataSource.filter = '';
    this.applyFilter();
  }

  /**
   * タグを比較してソートする。（指定キーワードと完全一致するタグを優先）
   * @param a - 比較する最初のタグ
   * @param b - 比較する2番目のタグ
   * @param keyword - キーワード
   * @returns
   */
  private compareTags(a: string, b: string, keyword: string): number {
    const aLower = a.trim().toLowerCase();
    const bLower = b.trim().toLowerCase();

    const aMatches = aLower === keyword;
    const bMatches = bLower === keyword;

    if (aMatches && !bMatches) return -1;
    if (!aMatches && bMatches) return 1;

    // 完全一致がない場合は昇順でソート
    return aLower.localeCompare(bLower);
  }

  public setFilters() {
    // ソートとページングの設定
    if (this.sort && this.sortState) {
      const sortState: Sort = {
        active: this.sortState.active,
        direction: this.sortState.direction,
      };

      this.filterService.setSort(this.dataSource, this.sort, sortState);
    }

    // ページングを設定
    if (this.paginator && this.pageIndex) {
      this.setPaginationIndex(this.pageIndex);
    }
  }

  /**
   * ページネーションの番号を設定する
   */
  private setPaginationIndex(pageIndex: number = 0) {
    setTimeout(() => {
      this.paginator.pageIndex = pageIndex!;
      this.cdr.detectChanges();
    }, 0);
  }

  /**
   * 比較するための氏名を取得する
   * @param user ユーザー情報
   * @returns 氏名のカナ
   */
  private getFullNameToCompare(user: AccountViewModel): string {
    return user.familyNameKana && user.givenNameKana
      ? `${user.familyNameKana} ${user.givenNameKana}`
      : '';
  }

  /**
   * 権限名取得
   */
  public get roleName(): string {
    return this.roleNameMap[this.selectedUserType] || '';
  }

  /**
   * 権限ごとにテーブルカラムを設定する
   * @param userType 権限
   */
  private _setDisplayedColumns(
    userType: UserTypeViewModel,
    role: RoleViewModel | undefined,
  ) {
    let tenantColumns = ['tenant'];
    let optionColumns = ['configurable', 'deviceTagList'];
    if (userType === 'system_admin') {
      tenantColumns = [];
      optionColumns = [];
    } else if (userType === 'tenant_admin') {
      optionColumns = [];
    }

    if (role !== 'system_admin') {
      tenantColumns = [];
    }

    const columns = [
      'checkbox',
      ...tenantColumns,
      'userName',
      'userId',
      'emailStatus',
      'mfaRequired',
      'mfaStatus',
      ...optionColumns,
      'lastLoginAt',
      'operation',
    ];
    this.displayedColumns = columns;
  }

  /**
   * テーブルに表示するデータソースを切り替える
   *
   * @param accountList アカウント一覧
   * @param userType ユーザー種別
   */
  private _setDataSource(
    accountList: AccountViewModel[],
    userType: UserTypeViewModel,
  ) {
    this.dataSource.data = accountList.filter(
      (account) => account.userType === userType,
    );
    this.dataSource.sort = this.sort;
    this.selection.clear();
  }

  /**
   * 各フィルターをリセットする
   */
  private resetFilters() {
    this.dataSource.filter = '';
    this.tenantNameKeyword = '';
    this.userNameKeyword = '';
    this.emailKeyword = '';
    this.selectedEmailStatus = undefined;
    this.selectedMfaRequired = undefined;
    this.selectedMfaStatus = undefined;
    this.selectedConfigurable = undefined;
    this.deviceTagKeyword = '';
    this.lastLoginAtKeyword = '';
  }

  /** 権限名マップ */
  private readonly roleNameMap: Record<UserTypeViewModel, string> = {
    system_admin: 'マクニカ管理者',
    tenant_admin: 'テナント管理者',
    tenant_user: 'テナントユーザー',
  };

  /**
   * フィルターをセッションストレージから読み込む。
   */
  private loadFilters(reset?: boolean): void {
    this.initUserType = this.selectedUserType;
    this.filterService.setSessionStorageKey(this.initUserType);

    const filters = this.filterService.getFiltersFromSessionStorage(reset);
    this.selectedTenant = filters.tenantName || '';
    this.userNameKeyword = filters.userName || '';
    this.emailKeyword = filters.email || '';
    this.selectedEmailStatus =
      typeof filters.emailStatus === 'boolean'
        ? filters.emailStatus
        : undefined;
    this.selectedMfaRequired =
      typeof filters.mfaRequiredStatus === 'boolean'
        ? filters.mfaRequiredStatus
        : undefined;
    this.selectedMfaStatus =
      typeof filters.mfaStatus === 'boolean' ? filters.mfaStatus : undefined;
    this.selectedConfigurable =
      typeof filters.configurable === 'boolean'
        ? filters.configurable
        : undefined;
    this.deviceTagKeyword = filters.deviceTag || '';
    this.lastLoginAtKeyword = filters.lastLoginAt || '';
    this.sortState = filters.sortState || this.initSortState;
    this.pageIndex = filters.pageIndex;
  }

  /**
   * フィルター条件をセッションストレージに保存する
   */
  private saveFiltersToStorage(): void {
    const filterList: AccountsFilter = {
      tenantName: this.selectedTenant,
      userName: this.userNameKeyword,
      email: this.emailKeyword,
      emailStatus: this.selectedEmailStatus,
      mfaRequiredStatus: this.selectedMfaRequired,
      mfaStatus: this.selectedMfaStatus,
      configurable: this.selectedConfigurable,
      deviceTag: this.deviceTagKeyword,
      lastLoginAt: this.lastLoginAtKeyword,
    };
    this.filterService.setFiltersToSessionStorage(filterList);
  }
}
